import { supabase } from './supabaseClient';
import logger from './utils/logger';

/**
 * Recommendation Service
 * Provides intelligent teacher recommendations based on student preferences
 */

/**
 * Calculate match score between student preferences and teacher profile
 * @param {Object} studentPrefs - Student preferences from onboarding
 * @param {Object} teacher - Teacher profile
 * @returns {number} Match score (0-100)
 */
function calculateMatchScore(studentPrefs, teacher) {
  let score = 0;
  let maxScore = 0;

  // 1. Subject Match (40 points) - HIGHEST PRIORITY
  maxScore += 40;
  if (studentPrefs.preferred_subjects && studentPrefs.preferred_subjects.length > 0 && teacher.subjects) {
    const studentSubjects = studentPrefs.preferred_subjects.map(s => s.toLowerCase());
    const teacherSubjects = teacher.subjects.map(s => s.toLowerCase());

    // Count how many subjects match
    const matchingSubjects = studentSubjects.filter(subject =>
      teacherSubjects.includes(subject)
    );

    if (matchingSubjects.length > 0) {
      // Full points if all student subjects are covered, partial points for some matches
      score += 40 * (matchingSubjects.length / studentSubjects.length);
    }
  }

  // 2. Tutoring Type Match (20 points)
  maxScore += 20;
  if (studentPrefs.preferred_tutoring_type && teacher.lesson_format_preference) {
    const studentType = studentPrefs.preferred_tutoring_type.toLowerCase();
    const teacherType = teacher.lesson_format_preference.toLowerCase();

    // Exact match or "both" on either side
    if (studentType === teacherType ||
        studentType === 'both' ||
        teacherType === 'both' ||
        teacherType === 'flexible') {
      score += 20;
    }
  }

  // 3. Language Match (15 points)
  maxScore += 15;
  if (studentPrefs.preferred_language && teacher.languages) {
    const studentLang = studentPrefs.preferred_language.toLowerCase();
    const teacherLangs = Array.isArray(teacher.languages)
      ? teacher.languages.map(l => l.toLowerCase())
      : [teacher.languages.toLowerCase()];

    if (studentLang === 'both' || teacherLangs.includes('both')) {
      score += 15;
    } else if (teacherLangs.includes(studentLang)) {
      score += 15;
    } else if (teacherLangs.length > 1) {
      // Partial credit if teacher speaks multiple languages
      score += 7;
    }
  }

  // 4. Session Type Match (15 points)
  maxScore += 15;
  if (studentPrefs.preferred_session_type && teacher.lesson_format_preference) {
    const studentSession = studentPrefs.preferred_session_type.toLowerCase();

    // Teachers can typically do both individual and group
    if (studentSession === 'flexible') {
      score += 15;
    } else if (studentSession === 'individual') {
      // Most teachers do individual sessions
      score += 15;
    } else if (studentSession === 'group') {
      // Give partial credit - many teachers can do group sessions
      score += 10;
    }
  }

  // 5. Rating Boost (10 points)
  maxScore += 10;
  if (teacher.rating && teacher.rating.avg_rating > 0) {
    // Convert 5-star rating to 10 points max
    score += (teacher.rating.avg_rating / 5) * 10;
  }

  // Normalize score to 0-100 range
  const normalizedScore = maxScore > 0 ? (score / maxScore) * 100 : 0;

  return Math.round(normalizedScore);
}

/**
 * Get recommended teachers based on student preferences
 * @param {string} studentId - Student UUID
 * @param {number} limit - Number of recommendations to return (default: 4)
 * @returns {Object} { recommendations: Array, onboardingCompleted: boolean }
 */
export async function getRecommendedTeachers(studentId, limit = 4) {
  try {
    console.log('[Recommendations] Fetching recommendations for student:', studentId);
    
    // 1. Fetch student preferences
    const { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('onboarding_completed, preferred_subjects, learning_goals, preferred_tutoring_type, preferred_session_type, preferred_language')
      .eq('uuid_id', studentId)
      .single();

    if (studentError) {
      logger.error('[Recommendations] Error fetching student preferences:', studentError);
      // Still try to return top-rated teachers as fallback
      const { data: fallbackTeachers } = await supabase
        .from('Teachers')
        .select('uuid_id, full_name, email, profile_photo, subjects, grade_levels, hourly_rate, bio, lesson_format_preference, languages, gender')
        .limit(limit);
      
      if (fallbackTeachers && fallbackTeachers.length > 0) {
        console.log('[Recommendations] Returning fallback teachers due to student data error');
        return { 
          recommendations: fallbackTeachers.map(t => ({ ...t, rating: { avg_rating: 0, ratings_count: 0 } })), 
          onboardingCompleted: false 
        };
      }
      
      return { recommendations: [], onboardingCompleted: false };
    }
    
    logger.debug('[Recommendations] Student data:', { 
      onboarding_completed: studentData?.onboarding_completed,
      has_preferred_subjects: !!studentData?.preferred_subjects?.length
    });

    // Check if onboarding is completed
    const onboardingCompleted = studentData && studentData.onboarding_completed === true;
    
    // If onboarding not completed, we'll still try to get some basic recommendations
    // but use a simpler approach (just top-rated teachers)
    if (!onboardingCompleted) {
      // Get top-rated teachers as fallback when onboarding isn't completed
      const { data: allTeachers, error: allTeachersError } = await supabase
        .from('Teachers')
        .select('uuid_id, full_name, email, profile_photo, subjects, grade_levels, hourly_rate, bio, lesson_format_preference, languages, gender')
        .limit(limit);
      
      if (!allTeachersError && allTeachers && allTeachers.length > 0) {
        // Get ratings for fallback recommendations
        const teacherEmails = allTeachers.map(t => t.email);
        let ratingsData = {};
        try {
          const { getMultipleTeacherRatings } = await import('./bookingService');
          const ratingsResult = await getMultipleTeacherRatings(teacherEmails);
          ratingsData = ratingsResult.data || {};
        } catch (ratingError) {
          logger.error('Error fetching ratings:', ratingError);
        }
        
        // Sort by rating and return top teachers
        const teachersWithRatings = allTeachers.map(teacher => {
          const rating = ratingsData[teacher.email] || { avg_rating: 0, ratings_count: 0 };
          return { ...teacher, rating };
        });
        
        teachersWithRatings.sort((a, b) => {
          const ratingA = a.rating?.avg_rating || 0;
          const ratingB = b.rating?.avg_rating || 0;
          return ratingB - ratingA;
        });
        
        return {
          recommendations: teachersWithRatings.slice(0, limit),
          onboardingCompleted: false
        };
      }
      
      return { recommendations: [], onboardingCompleted: false };
    }

    // 2. Build query to fetch matching teachers
    // Fetch all teachers and filter by subjects client-side (more reliable than .overlaps())
    let teachers = [];
    let teachersError = null;

    // Fetch all teachers first
    const { data: allTeachers, error: allError } = await supabase
      .from('Teachers')
      .select('uuid_id, full_name, email, profile_photo, subjects, grade_levels, hourly_rate, bio, lesson_format_preference, languages, gender')
      .limit(50);
    
    if (allError) {
      teachersError = allError;
    } else if (allTeachers) {
      // If student has preferred subjects, filter teachers who teach those subjects
      if (studentData.preferred_subjects && studentData.preferred_subjects.length > 0) {
        const studentSubjectsLower = studentData.preferred_subjects.map(s => s.toLowerCase());
        
        // Filter teachers whose subjects overlap with student's preferred subjects
        const matchingTeachers = allTeachers.filter(teacher => {
          if (!teacher.subjects || !Array.isArray(teacher.subjects)) return false;
          const teacherSubjectsLower = teacher.subjects.map(s => s.toLowerCase());
          // Check if any student subject matches any teacher subject
          return studentSubjectsLower.some(subject => teacherSubjectsLower.includes(subject));
        });
        
        if (matchingTeachers.length > 0) {
          teachers = matchingTeachers;
        } else {
          // If no teachers match preferred subjects, fall back to all teachers
          logger.debug('No teachers found with matching subjects, falling back to all teachers');
          teachers = allTeachers;
        }
      } else {
        // If no preferred subjects, use all teachers
        teachers = allTeachers;
      }
    }

    if (teachersError) {
      logger.error('Error fetching teachers:', teachersError);
      return { recommendations: [], onboardingCompleted: true };
    }

    if (!teachers || teachers.length === 0) {
      logger.debug('[Recommendations] No teachers found in database');
      return { recommendations: [], onboardingCompleted: true };
    }
    
    logger.debug('[Recommendations] Found', teachers.length, 'teachers to score');

    // 3. Fetch ratings for all teachers
    const teacherEmails = teachers.map(t => t.email);
    let ratingsData = {};

    try {
      // Import booking service dynamically to avoid circular dependency
      const { getMultipleTeacherRatings } = await import('./bookingService');
      const ratingsResult = await getMultipleTeacherRatings(teacherEmails);
      ratingsData = ratingsResult.data || {};
    } catch (ratingError) {
      logger.error('Error fetching ratings:', ratingError);
    }

    // 4. Calculate match scores and attach ratings
    const teachersWithScores = teachers.map(teacher => {
      const rating = ratingsData[teacher.email] || { avg_rating: 0, ratings_count: 0 };
      const teacherWithRating = { ...teacher, rating };
      const matchScore = calculateMatchScore(studentData, teacherWithRating);

      return {
        ...teacherWithRating,
        matchScore,
        matchPercentage: matchScore // For display purposes
      };
    });

    // 5. Sort by match score (highest first) and limit results
    teachersWithScores.sort((a, b) => b.matchScore - a.matchScore);
    
    // Filter out teachers with 0 match score, but if all are 0, still show top-rated ones
    const validRecommendations = teachersWithScores.filter(t => t.matchScore > 0);
    
    // If we have valid recommendations, use them. Otherwise, show top-rated teachers as fallback
    let recommendations;
    if (validRecommendations.length > 0) {
      recommendations = validRecommendations.slice(0, limit);
    } else {
      // Fallback: Show top-rated teachers even if match score is 0
      // Sort by rating instead
      teachersWithScores.sort((a, b) => {
        const ratingA = a.rating?.avg_rating || 0;
        const ratingB = b.rating?.avg_rating || 0;
        return ratingB - ratingA;
      });
      recommendations = teachersWithScores.slice(0, limit);
    }

    return {
      recommendations,
      onboardingCompleted: true
    };

  } catch (error) {
    logger.error('[Recommendations] Error in getRecommendedTeachers:', error);
    // Last resort: try to get any teachers
    try {
      const { data: fallbackTeachers } = await supabase
        .from('Teachers')
        .select('uuid_id, full_name, email, profile_photo, subjects, grade_levels, hourly_rate, bio, lesson_format_preference, languages, gender')
        .limit(limit);
      
      if (fallbackTeachers && fallbackTeachers.length > 0) {
        logger.debug('[Recommendations] Returning fallback teachers due to error');
        return { 
          recommendations: fallbackTeachers.map(t => ({ ...t, rating: { avg_rating: 0, ratings_count: 0 } })), 
          onboardingCompleted: false 
        };
      }
    } catch (fallbackError) {
      logger.error('[Recommendations] Fallback query also failed:', fallbackError);
    }
    
    return { recommendations: [], onboardingCompleted: false };
  }
}

/**
 * Get hybrid recommendations combining preference-based and history-based
 * @param {string} studentId - Student UUID
 * @param {number} limit - Number of recommendations to return
 * @returns {Object} { recommendations: Array, onboardingCompleted: boolean }
 */
export async function getHybridRecommendations(studentId, limit = 4) {
  try {
    // Get preference-based recommendations
    const preferenceResult = await getRecommendedTeachers(studentId, limit);

    // If onboarding not completed, return early
    if (!preferenceResult.onboardingCompleted) {
      return preferenceResult;
    }

    // Get booking history to boost teachers student has worked with before
    const { data: bookingHistory } = await supabase
      .from('booking_requests')
      .select('teacher_email')
      .eq('student_id', studentId)
      .not('teacher_email', 'is', null);

    if (bookingHistory && bookingHistory.length > 0) {
      const previousTeacherEmails = [...new Set(bookingHistory.map(b => b.teacher_email))];

      // Boost scores for teachers student has worked with before
      preferenceResult.recommendations = preferenceResult.recommendations.map(teacher => {
        if (previousTeacherEmails.includes(teacher.email)) {
          return {
            ...teacher,
            matchScore: Math.min(100, teacher.matchScore + 10), // Boost by 10 points
            isPreviousTeacher: true
          };
        }
        return teacher;
      });

      // Re-sort after boosting
      preferenceResult.recommendations.sort((a, b) => b.matchScore - a.matchScore);
    }

    return preferenceResult;

  } catch (error) {
    logger.error('Error in getHybridRecommendations:', error);
    return { recommendations: [], onboardingCompleted: false };
  }
}

export default {
  getRecommendedTeachers,
  getHybridRecommendations,
  calculateMatchScore
};
